import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { LocalizationProvider, CalendarPicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RadioGroup } from './RadioGroup';
import { SectionBase } from './SectionBase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { CalendarContainer } from '../styles';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { formattedDate } from 'shared/utils/date';
import { createDate } from 'shared/utils/formatUTC';

// backward compatibility
const parseDateValue = value => {
  if (!value) return undefined;
  if (typeof value === 'string') return createDate(value);
  if (!value.date) return undefined;
  if (typeof value.date === 'string') return createDate(value.date);
  return value.date;
};

export const SectionDate = ({
  title,
  subTitle,
  helpText,
  options,
  questionId,
  value,
  onChange,
  error,
  guidanceText,
}) => {
  const handleOption = useCallback(
    event => {
      onChange(questionId, {
        ...value,
        option: event.target.value,
      });
    },
    [questionId, value, onChange],
  );
  const handleDate = useCallback(
    date => {
      onChange(questionId, {
        ...value,
        date,
      });
    },
    [questionId, value, onChange],
  );
  useEffect(() => {
    if (typeof value !== 'string') {
      return;
    }

    handleDate(createDate(value));
  }, [value, handleDate]);

  const date = parseDateValue(value);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <SectionBase
        title={title}
        subTitle={subTitle}
        helpText={helpText}
        error={error}
        questionId={questionId}
        guidanceText={guidanceText}
      >
        {options && (
          <RadioGroup
            options={options}
            subTitle={subTitle}
            value={value?.option}
            onChange={handleOption}
          />
        )}
        <DateContainerDiv>
          {questionId === 10006 && (
            <TrueImpactText variant="body-bold">
              Planning Start Date:
            </TrueImpactText>
          )}
          <div className="show-selected-date">
            <div>{formattedDate(date) || 'Please select a date below'}</div>
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <CalendarContainer className="show-cn-calendar">
            <CalendarPicker date={date || new Date()} onChange={handleDate} />
          </CalendarContainer>
        </DateContainerDiv>
      </SectionBase>
    </LocalizationProvider>
  );
};

export const DateContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small}
  align-items: center;
  justify-content: center;
  .show-selected-date {
    width: 100%;
    height: 61px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid;
    padding-left: 14px;
    padding-right: 24px;
    border-radius: 4px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
  .MuiPickersBasePicker-pickerView {
    left: 10px !important;
  }
  .MuiPickersStaticWrapper-staticWrapperRoot {
    padding: ${SPACERS.small};
    background-color: ${COLORS.greyLight};
    border-radius: 4px;
  }
  .MuiIconButton-label {
    width: 40px;
    height: 40px;
    padding: ${SPACERS.tiny};
    svg {
      width: 24px;
      height: 24px;
    }
  }
  .MuiFormControl-marginNormal {
    margin-top: 0;
    width: 100%;
  }
`;
